import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { TextareaInputField } from "@/components/shared/form";
import { useArchiveTenderMutationHook, useUnarchiveTenderMutationHook } from "@/hooks";
import { useTranslation } from "react-i18next";
import { useForm, SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { CircleLoader } from "@/components/shared/loaders";
import { useParams } from "react-router-dom";
import { FetchTenderByIdQuery } from "@/graphql/__generated__/graphql";

interface ArchiveTenderComponentProps {
  tenderByIdData?: FetchTenderByIdQuery | null;
}

/**
 * Zod schema for archive tender form validation
 * Reason: Required string field with minimum length
 */
const createArchiveFormSchema = (t: (_key: string) => string) => {
  return z.object({
    reason: z.string().min(1, t("edittender.options.archivetender.reasonrequired") || "Reason is required")
  });
};

type ArchiveFormData = z.infer<ReturnType<typeof createArchiveFormSchema>>;

/**
 * Component for archiving and unarchiving a tender.
 * Shows a form to archive with a reason if not archived, or displays the archive reason and unarchive button if archived.
 *
 * @component
 * @param {FetchTenderByIdQuery | null | undefined} tenderByIdData - The tender data from the query
 */
function ArchiveTenderComponent({ tenderByIdData }: ArchiveTenderComponentProps) {
  const { t } = useTranslation(["translation", "toasts"]);
  const { tenderID } = useParams<{ tenderID: string }>();

  const { archiveTenderMutation, loading: archiveLoading } = useArchiveTenderMutationHook();
  const { unarchiveTenderMutation, loading: unarchiveLoading } = useUnarchiveTenderMutationHook();

  // Extract archived and archiveReason from tenderByIdData
  const archived = tenderByIdData?.fetchTenderById?.archived;
  const archiveReason = tenderByIdData?.fetchTenderById?.archiveReason;

  const FormSchema = createArchiveFormSchema(t);

  const form = useForm<ArchiveFormData>({
    resolver: zodResolver(FormSchema),
    defaultValues: {
      reason: ""
    },
    mode: "onChange"
  });

  const onSubmit: SubmitHandler<ArchiveFormData> = async (data) => {
    await archiveTenderMutation({
      variables: {
        tenderId: Number(tenderID),
        reason: data.reason
      }
    });
    form.reset();
  };

  const handleUnarchive = async () => {
    await unarchiveTenderMutation({
      variables: {
        tenderId: Number(tenderID)
      }
    });
  };

  const isArchived = archived === true;
  const isLoading = archiveLoading || unarchiveLoading;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold">{t("edittender.options.archivetender.title")}</CardTitle>
        <CardDescription>
          {
            isArchived ? t("edittender.options.archivetender.unarchivedescription") : t("edittender.options.archivetender.archivedescription")
          }
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!isArchived ? (
          <div>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                <TextareaInputField
                  form={form}
                  name="reason"
                  label={t("edittender.options.archivetender.reasonlabel")}
                  placeholder={t("edittender.options.archivetender.reasonplaceholder")}
                  formItemClassName="w-full"
                />
                <Button
                  type="submit"
                  variant="outline"
                  disabled={isLoading || !form.formState.isValid}
                >
                  {archiveLoading ? (
                    <div className="flex items-center gap-2">
                      <CircleLoader
                        visible={true}
                        height={20}
                        width={20}
                        strokeWidth={4}
                        ariaLabel="circle-loader"
                      />
                      {t("edittender.options.archivetender.btnarchiving")}
                    </div>
                  ) : (
                    t("edittender.options.archivetender.btnarchive")
                  )}
                </Button>
              </form>
            </Form>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <p className="text-sm font-medium mb-2">
                {t("edittender.options.archivetender.archivedreason")}
              </p>
              <p className="text-sm text-muted-foreground bg-muted px-3 py-2 rounded-md">
                {archiveReason}
              </p>
            </div>
            <Button
              variant="outline"
              onClick={handleUnarchive}
              disabled={isLoading}
            >
              {unarchiveLoading ? (
                <div className="flex items-center gap-2">
                  <CircleLoader
                    visible={true}
                    height={20}
                    width={20}
                    strokeWidth={4}
                    ariaLabel="circle-loader"
                  />
                  {t("edittender.options.archivetender.btnunarchiving")}
                </div>
              ) : (
                t("edittender.options.archivetender.btnunarchive")
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default ArchiveTenderComponent;
